import React, { useState, useEffect } from 'react';
import { Plus, Search, Building, Edit, Trash2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { styles, Modal } from '../theme';

import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';

const BankDetails = ({ token }) => {
  const { canApprove, canCreate, canUpdate, canDelete, getModuleBranches } = usePermissions();
  const [bankDetails, setBankDetails] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [branches, setBranches] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ chart_of_account_id: '', bank_account_number: '', bank_account_name: '', branch_id: '' });
  const [filter, setFilter] = useState({
    branch_id: '',
    search: ''
  });

  useEffect(() => {
    fetchBankDetails(currentPage, perPage, searchQuery, filter.branch_id);
    fetchBranches();
    fetchChartOfAccounts();
  }, [currentPage, perPage, searchQuery, filter.branch_id]);

  const fetchBankDetails = async (page = 1, limit = 10, search = '', branchId = '') => {
    try {
      const queryParams = new URLSearchParams({
        page,
        per_page: limit,
        search: search,
        branch_id: branchId
      });

      const res = await fetchWithAuth(`${API_URL}/bank-details?${queryParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();

      setBankDetails(data.details);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      console.error('Error fetching bank details:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBranches(data);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchChartOfAccounts = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/chart_of_accounts/all`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setChartOfAccounts(data);
    } catch (err) {
      console.error('Error fetching chart of accounts:', err);
    }
  };

  const handleSubmit = async () => {
    if (!form.chart_of_account_id || !form.bank_account_number || !form.bank_account_name || !form.branch_id) {
      alert('Missing required fields (Chart of Account, Account Number, Account Name, Branch)');
      return;
    }

    try {
      const url = editingId ? `${API_URL}/bank-details/${editingId}` : `${API_URL}/bank-details`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        fetchBankDetails();
        setShowForm(false);
        setEditingId(null);
        setForm({ chart_of_account_id: '', bank_account_number: '', bank_account_name: '', branch_id: '' });
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error saving bank detail:', err);
    }
  };

  const handleEdit = (detail) => {
    setForm({
      chart_of_account_id: detail.chart_of_account_id || '',
      bank_account_number: detail.bank_account_number || '',
      bank_account_name: detail.bank_account_name || '',
      branch_id: detail.branch_id || ''
    });
    setEditingId(detail.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bank detail?')) return;
    try {
      await fetchWithAuth(`${API_URL}/bank-details/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchBankDetails();
    } catch (err) {
      console.error('Error deleting bank detail:', err);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Bank Details</h1>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ chart_of_account_id: '', bank_account_number: '', bank_account_name: '', branch_id: '' }); }} style={styles.button}>
          <Plus style={{ width: '20px', height: '20px' }} /> Add Bank Detail
        </button>
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={`${editingId ? 'Edit' : 'Add'} Bank Detail`}
        maxWidth="600px"
      >
        <div style={styles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Chart of Account *</label>
            <select value={form.chart_of_account_id} onChange={(e) => setForm({ ...form, chart_of_account_id: e.target.value })} style={styles.input}>
              <option value="">Select Account</option>
              {chartOfAccounts.map(c => <option key={c.id} value={c.id}>{c.account_name}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Bank Account Name *</label>
            <input type="text" value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Bank Account Number *</label>
            <input type="text" value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch *</label>
            <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} style={styles.input}>
              <option value="">Select Branch</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Save</button>
        </div>
      </Modal>

      {/* Filters */}
      <div style={{
        ...styles.card,
        marginBottom: '20px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px'
      }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Filter by Branch</label>
          <select
            value={filter.branch_id}
            onChange={(e) => { setFilter({ ...filter, branch_id: e.target.value }); setCurrentPage(1); }}
            style={styles.input}
          >
            <option value="">All Branches</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search</label>
          <input
            type="text"
            placeholder="Search by account number or name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            style={styles.input}
          />
        </div>
      </div>

      <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Account Name</th>
              <th style={styles.th}>Account Number</th>
              <th style={styles.th}>Linked COA</th>
              <th style={styles.th}>Branch</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bankDetails.map(bd => (
              <tr key={bd.id}>
                <td style={styles.td}>{bd.bank_account_name}</td>
                <td style={styles.td}>{bd.bank_account_number}</td>
                <td style={styles.td}>{bd.chart_of_account_name || '-'}</td>
                <td style={styles.td}>{bd.branch_name || '-'}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  {canUpdate('bank_details') && (
                    <button onClick={() => handleEdit(bd)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>
                      <Edit size={18} />
                    </button>
                  )}
                  {canDelete('bank_details') && (
                    <button onClick={() => handleDelete(bd.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                      <Trash2 size={18} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Showing {totalCount === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalCount)} to {Math.min(currentPage * perPage, totalCount)} of {totalCount} entries
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            >
              {[5, 10, 25, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronLeft size={16} />
            </button>

            <div style={{ display: 'flex', gap: '4px', margin: '0 8px' }}>
              {[...Array(totalPages || 0)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: currentPage === pageNum ? '#4f46e5' : 'white',
                        color: currentPage === pageNum ? 'white' : '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  (pageNum === 2 && currentPage > 3) ||
                  (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                ) {
                  return <span key={pageNum} style={{ padding: '6px' }}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{
                ...styles.iconButton,
                padding: '6px',
                opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                border: '1px solid #d1d5db'
              }}
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default BankDetails;
